import dayjs from "dayjs";
import { useTheme } from "next-themes";
import { useEffect, useRef, useState } from "react";
import { format2, format4, numberWithCommas, separateNumberWithoutFormat } from "../utils/numbers";

import Modal from "bootstrap/js/dist/modal";
import TransactionDetail from "./modals/transaction-detail";
import { useAuth } from "../store/AuthContext";

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from "react-i18next";

dayjs.extend(utc);
dayjs.extend(timezone);

function parseDate(item) {
  if (item?.txnData?.settlement_date != null) {
    return dayjs(item?.txnData?.settlement_date);
  }
  return dayjs(item?.txnData?.settlement_date);
}

function formatTimeStr(time) {
  return dayjs(time).format("h:mm A");
}

function getDateLabel(dateObj) {
  const day = dateObj.startOf("day");
  const today = dayjs().startOf("day");
  const diff = today.diff(day, "day");

  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return day.format("DD-MMM-YYYY");
}

function getTransactionType(txn, user) {
  let t = "";
  switch (txn.txnr_type) {
    case "redemptionofunits":
      t = "Redemption";
      break;
    case "saleofunit":
      t = "Investment";
      break;
    case "conversionin":
      t = "Conversion In";
      break;
    case "conversionout":
      t = "Conversion Out";
      break;
    case "transferofunits":
      if (txn.folio_no === user?.data.folio_no) {
        t = "Transfer Out";
      } else {
        t = "Transfer In";
      }
      break;
  }
  return t;
}

function getTransactionColor(txn, user, theme) {
  switch (txn.txnr_type) {
    case "redemptionofunits":
      return theme === 'dark' ? "rgb(238, 68, 59)" : "rgb(217, 44, 35)";
    case "saleofunit":
      return theme === 'dark' ? "rgb(15, 189, 23)" : "rgb(8, 167, 16)";
    case "conversionout":
      return theme === 'dark' ? "rgb(238, 68, 59)" : "rgb(217, 44, 35)";
    case "conversionin":
      return theme === 'dark' ? "rgb(15, 189, 23)" : "rgb(8, 167, 16)";
    case "transferofunits":
      if (txn.folio_no === user?.data.folio_no) {
        return theme === 'dark' ? "rgb(238, 68, 59)" : "rgb(217, 44, 35)";
      } else {
        return theme === 'dark' ? "rgb(15, 189, 23)" : "rgb(8, 167, 16)";
      }
  }
}

export default function Transactions({ data }) {
  const [screenSize, setScreenSize] = useState();
  const [activeTransaction, setActiveTransaction] = useState(null);
  const { theme } = useTheme();
  const { user } = useAuth();

  const { t } = useTranslation();

  const transactionModalRef = useRef(null);
  const transactionModalInstanceRef = useRef(null);

  const sorted = [...data].sort((a, b) => parseDate(a).diff(parseDate(b)));

  const groups = sorted.reduce((map, item) => {
    const dateObj = parseDate(item);
    const label = getDateLabel(dateObj);
    if (!map.has(label)) map.set(label, []);
    map.get(label).push({ ...item, _dateObj: dateObj });
    return map;
  }, new Map());

  function handleTransactionSelect(txnData) {
    setActiveTransaction(txnData);

    if (transactionModalRef.current) {
      transactionModalInstanceRef.current?.toggle();
    }
  }

  useEffect(() => {
    if (transactionModalRef.current) {
      transactionModalInstanceRef.current = new Modal(transactionModalRef.current, { backdrop: true, keyboard: true });
    }

    const resizeScreen = () => {
      setScreenSize(window?.innerWidth);
    };

    resizeScreen();
    window.addEventListener("resize", resizeScreen);

    return () => {
      window.removeEventListener("resize", resizeScreen);
    };
  }, []);

  if (data.length === 0 || data === null || data === undefined) {
    return (
      <div
        className='w-100 position-absolute start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
        style={{
          color: theme === "light" ? "rgb(0,0,0)" : "rgb(255,255,255)",
          top: "50%",
        }}
      >
        <div
          className=''
          role='status'
          style={{
            fontSize: "1.2rem",
            color: theme === "dark" ? "rgb(200,200,200)" : "rgba(80, 80, 80, 1)",
          }}
        >
          {t("app.messages.no.transactions")}
        </div>
      </div>
    );
  }

  return (
    <div className={`mt-3 flex-column flex-nowrap p-0 pe-sm-1 pb-2 pb-sm-0${(theme === "dark" ? " add-custom-scroll-dark" : " add-custom-scroll")}`} style={{
      overflowY: 'auto',
      scrollbarWidth: screenSize >= 576 ? '' : "none",
            height: screenSize >= 992
                  ? "calc(100dvh - 275px)"
                  : screenSize <= 575
                    ? "calc(100dvh - 275px)"
                    : "calc(100dvh - 348px)",
    }}>
      {[...groups.entries()].map(([label, items], index) => (
        <div
          className='mb-3'
          key={index}
          style={{
            fontSize: "1.2rem",
            color: theme === "dark" ? "rgb(138, 182, 234)" : "rgb(54, 55, 75)",
          }}
        >
          <div className='ps-2'>{label}</div>
          <div
            className='w-100 mt-3'
            style={{
              display: "grid",
              maxWidth: "100%",
              gridTemplateColumns: screenSize >= 1200 ? "1fr 1fr 1fr" : screenSize <= 1199 && screenSize >= 768 ? "1fr 1fr" : "1fr",
              gap: "12px",
            }}
          >
            {items.map((txn, idx) => {
              const balanceInMinus = String(txn?.current_balance)[0] === "-";
              const balanceHasDecimal = String(txn?.current_balance).includes(".");
              const balance = balanceInMinus
                ? separateNumberWithoutFormat(balanceHasDecimal ? format4(txn?.current_balance).slice(1) : format4(txn?.current_balance).slice(1))
                : separateNumberWithoutFormat(balanceHasDecimal ? format4(txn?.current_balance) : format4(txn?.current_balance));

              const units = separateNumberWithoutFormat(txn.txnData?.units);
              const amount = separateNumberWithoutFormat(format2(txn.txnData?.gross_amount));

              return (
                <div className='col-12 col-sm p-0 animate-card' key={idx} style={{ minWidth: 0 }}>
                  <button
                    type='button'
                    className='row mx-auto align-items-center p-3 flex-nowrap column-gap-3 m-0 w-100'
                    onClick={() => handleTransactionSelect(txn)}
                    style={{
                      border: "1px solid",
                      background: theme === 'dark' ? 'rgb(27, 33, 43)' : 'rgb(210 218 235)',
                      borderColor: theme === 'dark' ? 'rgb(63, 69, 93)' : 'rgb(143 170 192)',
                      boxShadow: theme === 'dark' ? '0px 0px 3px 1px rgba(12, 13, 17, .3)' : 'rgb(97 97 99 / 15%) 0px 0px 3px 1px',
                      borderRadius: "12px",
                    }}
                  >
                    <div
                      className='col-auto p-0 d-flex align-items-center justify-content-center flex-shrink-0'
                      style={{
                        width: screenSize === "sm" ? "54px" : "62px",
                        height: screenSize === "sm" ? "54px" : "62px",
                        color: "rgb(185, 185, 185)",
                        border: "1px solid rgb(64, 103, 160)",
                        background: "linear-gradient(to right bottom, rgb(62, 89, 136), rgb(27, 63, 107))",
                        fontWeight: "600",
                        fontSize: screenSize === "sm" ? "1.1rem" : "1.2rem",
                        borderRadius: "12px",
                        minWidth: 0,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textAlign: "center",
                      }}
                    >
                      {/* {account.value.bank_name?.split(' ').map((word) => {
                                return word[0]
                              })} */}
                      {txn.fund_code.slice(0, 4)}
                    </div>
                    <div className='col p-0 d-flex flex-column justify-content-center align-items-start' style={{ minWidth: 0 }}>
                      <div
                        className='text-start'
                        style={{
                          fontSize: "1.1rem",
                          lineHeight: "1.1",
                          color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          display: "-webkit-box",
                          boxOrient: "vertical",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: "2",
                          overflow: "hidden",
                        }}
                      >
                        {getTransactionType(txn.txnData, user)}
                      </div>
                      <div
                        className='text-start'
                        style={{
                          fontSize: "14px",
                          marginTop: "5px",
                          lineHeight: "1.1",
                          color: theme === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(40, 40, 40, 0.8)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "100%",
                        }}
                      >
                        {txn.fund_name}
                      </div>
                      <div
                        className='d-flex align-items-center flex-nowrap column-gap-2'
                        style={{
                          fontSize: "1rem",
                          marginTop: "6px",
                          color: theme === "dark" ? "rgb(150, 150, 150)" : "rgba(58, 57, 57, 1)",
                        }}
                      >
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width={18}
                          height={18}
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth={theme === 'dark' ? 1.5 : 1.75}
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          className='icon icon-tabler icons-tabler-outline icon-tabler-clock-hour-4'
                          style={{
                            color: theme === "dark" ? "rgb(150, 150, 150)" : "rgba(22, 22, 162, 1)",
                          }}
                        >
                          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                          <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
                          <path d='M12 12l3 2' />
                          <path d='M12 7v5' />
                        </svg>
                        <span
                          className=''
                          style={{
                            fontSize: ".9rem",
                          }}
                        >
                          {formatTimeStr(txn.txnData?.settlement_time)}
                        </span>
                      </div>
                    </div>
                    <div className='col-auto p-0 d-flex flex-column justify-content-center align-items-end'>
                      <div
                        className=''
                        dir="ltr"
                        style={{
                          fontSize: "1rem",
                          lineHeight: "1.1",
                          color: getTransactionColor(txn.txnData, user, theme),
                        }}
                      >
                        <span
                          className=""
                          dir="ltr"
                          style={{
                            fontSize: ".85rem",
                            marginRight: '.25rem',
                            color: theme === "dark" ? "rgb(159, 159, 159)" : "rgba(60, 60, 60, 1)",
                          }}
                        >
                          {t("app.fund.rupee")}
                        </span>
                        <span className='' dir="ltr">
                          {numberWithCommas(amount.whole)}
                          {amount.hasDecimal ? (
                            <>
                              .
                              <span
                                style={{
                                  fontSize: ".85rem",
                                }}
                              >
                                {amount.decimal}
                              </span>
                            </>
                          ) : (
                            ""
                          )}
                        </span>
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "1rem",
                          lineHeight: "1.1",
                          color: getTransactionColor(txn.txnData, user, theme),
                          marginTop: "5px",
                        }}
                      >
                        {numberWithCommas(units.whole)}
                        {units.hasDecimal ? (
                          <>
                            .
                            <span
                              style={{
                                fontSize: ".85rem",
                              }}
                            >
                              {units.decimal}
                            </span>
                          </>
                        ) : (
                          ""
                        )}
                        <span
                          className='ms-1'
                          style={{
                            fontSize: ".85rem",
                            color: theme === "dark" ? "rgb(159, 159, 159)" : "rgba(60, 60, 60, 1)",
                          }}
                        >
                          {t("app.fund.units")}
                        </span>
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: ".85rem",
                          lineHeight: "1.1",
                          color: theme === "dark" ? "rgb(159, 159, 159)" : "rgba(60, 60, 60, 1)",
                          marginTop: "6px",
                        }}
                      >
                        {t("app.statement.balance")}
                        <span
                          className='ms-1'
                          style={{
                            fontSize: "1rem",
                            color: theme === "dark" ? "rgb(86, 174, 247)" : "rgb(42, 35, 144)",
                          }}
                        >
                          {balanceInMinus ? "-" + numberWithCommas(balance.whole) : numberWithCommas(balance.whole)}
                          {balance.hasDecimal ? (
                            <>
                              .
                              <span
                                style={{
                                  fontSize: ".85rem",
                                }}
                              >
                                {balance.decimal}
                              </span>
                            </>
                          ) : (
                            ""
                          )}
                        </span>
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* -- TXN DETAIL VIEW - MODAL -- */}
      <div className='modal fade' id='txnModal' tabIndex='-1' aria-labelledby='txnModal' aria-hidden='true' ref={transactionModalRef}>
        <div
          className='modal-dialog modal-dialog-centered'
          style={{
            ["--bs-modal-width"]: "800px",
            marginInline: screenSize >= 830 ? "auto" : ".5rem",
          }}
        >
          <div
            className='modal-content'
            style={{
              // backgroundColor: theme === "light" ? "rgb(243, 243, 243)" : "rgb(50, 50, 50)",
              borderRadius: "20px",
              border: "none",
            }}
          >
            <div className='modal-body p-0 position-relative'>
              <TransactionDetail data={activeTransaction} txnType={getTransactionType} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
